import { useEffect, useState } from 'react';
import type { Intervention } from '@/lib/types';
import { formatHeureMur, formatDurationMinutes, maintenantMurCasablanca } from '@/lib/dates';
import StatusBadge, { UrgentBadge } from './StatusBadge';

/**
 * Minutes restantes avant la fin planifiée d'une intervention EN_COURS.
 * Les DEUX bornes doivent être dans le même référentiel « heure-mur » :
 * - creneauFin = champ planifié (composants UTC)
 * - now        = maintenantMurCasablanca() (composants UTC = heure-mur Casa)
 * Comparer creneauFin à l'horloge device brute (new Date()) mélange deux
 * référentiels et décale le countdown jusqu'à 60 min (UTC+1 hors Ramadan).
 * Positif = il reste du temps ; négatif = débordement.
 */
export function remainingMinutes(creneauFin: string | Date, now: Date): number {
  const fin = typeof creneauFin === 'string' ? new Date(creneauFin) : creneauFin;
  return (fin.getTime() - now.getTime()) / 60_000;
}

type Props = {
  intervention: Intervention;
  onClick?: () => void;
  style?: React.CSSProperties;
};

export default function RdvCard({ intervention: i, onClick, style }: Props) {
  const isEnCours = i.statut === 'EN_COURS';

  // Halo pulsé + countdown : la card EN_COURS a besoin d'un timer rafraîchi
  // chaque 30s pour calculer "Reste X min" ou "+X min".
  // « now » DOIT être l'heure-mur Casablanca (même référentiel que creneauFin,
  // un champ planifié heure-mur=UTC), sinon décalage jusqu'à 60 min.
  const [now, setNow] = useState(() => maintenantMurCasablanca());
  useEffect(() => {
    if (!isEnCours) return;
    const id = setInterval(() => setNow(maintenantMurCasablanca()), 30_000);
    return () => clearInterval(id);
  }, [isEnCours]);

  // Urgent : bordure rouge épaisse en plus du filet gauche mécano
  const urgentBorderClass = i.urgent
    ? 'border-2 border-status-annule-border'
    : 'border border-border-light';

  const enCoursAnimClass = isEnCours ? 'animate-pulse-en-cours' : '';

  return (
    <div
      role="button"
      data-testid={`rdv-card-${i.id}`}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      style={{ borderLeftColor: i.mecanicien.couleur, ...style }}
      className={`relative bg-surface-white border-l-4 rounded-md p-3 shadow-sm hover:shadow-md transition-all cursor-grab focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ktm-orange ${urgentBorderClass} ${enCoursAnimClass}`}
    >
      {i.flagDecale && (
        <div className="absolute -top-1.5 left-3 bg-ktm-orange text-text-on-orange text-xs font-bold px-1.5 py-0.5 rounded-sm">
          ↪ Décalé
        </div>
      )}

      <div className="flex justify-between items-start gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-base font-semibold text-text-primary truncate">{i.nature}</div>
          <div className="text-sm text-text-secondary truncate">
            {i.moto.modele} · <span className="font-mono">{i.moto.vin}</span>
          </div>
        </div>
        <div className="flex flex-col gap-1 items-end shrink-0">
          {i.urgent && <UrgentBadge />}
          <StatusBadge statut={i.statut} />
        </div>
      </div>

      <div className="text-xs text-text-muted">
        {i.client.prenom} {i.client.nom}
      </div>

      <div className="flex justify-between items-center mt-3 text-xs font-mono text-text-muted">
        <span>
          {formatHeureMur(i.creneauDebut)} — {formatHeureMur(i.creneauFin)}
        </span>
        {isEnCours ? (
          <CountdownLabel creneauFin={i.creneauFin} now={now} />
        ) : (
          <span className="font-semibold">{formatDurationMinutes(i.dureeEstimeeMinutes)}</span>
        )}
      </div>
    </div>
  );
}

/**
 * Affichage live pour les interventions EN_COURS :
 * - reste > 0 : "Reste X min" (text-muted)
 * - reste <= 0 : "+X min" (rouge bold — débordement)
 */
function CountdownLabel({ creneauFin, now }: { creneauFin: string; now: Date }) {
  const remaining = remainingMinutes(creneauFin, now);
  if (remaining > 0) {
    return (
      <span className="font-mono font-semibold">Reste {Math.ceil(remaining)} min</span>
    );
  }
  const overflow = Math.abs(Math.floor(remaining));
  return (
    <span className="font-mono font-bold text-status-annule-text">+{overflow} min</span>
  );
}
